/**
 * Zod schema for the `rule_json` payload stored on every
 * `component_rules` row.
 *
 * The shape mirrors the TypeScript {@link ComponentRuleJsonDTO} used by
 * the service layer:
 *
 * ```ts
 * {
 *   conditions: ConditionGroup,
 *   effects: RuleEffect[],
 * }
 * ```
 *
 * The schema is used by the module-scoped raw-JSON CRUD surface on the
 * admin module edit page to validate what the admin types into the
 * monospaced textarea before it is POSTed to the backend. The backend
 * owns an equivalent validator; this one lives on the frontend so the
 * user gets immediate feedback without a network round-trip.
 *
 * Why `discriminatedUnion` for effects: every `RuleEffect` is a tagged
 * object, so a discriminated union gives clean per-type validation
 * messages (the admin sees "Expected 'prop' for setProp effect" instead
 * of a generic "Invalid input"). The `type` literal is also what the
 * HOC (Phase 6) will switch on.
 */

import { z } from 'zod';

import { conditionOperatorSchema } from '@components/integrated/entity-form/validation/conditionSchema';

import type {
  ConditionGroup,
  FieldCondition,
  RuleEffect,
  SimpleCondition,
} from '../types';

// ---------------------------------------------------------------------------
// Effect schema
// ---------------------------------------------------------------------------

/**
 * Allowed values inside a `filterPropValues` effect's `allowed` array.
 *
 * `string | number` matches the TypeScript type:
 * `{ type: 'filterPropValues'; prop: string; allowed: Array<string | number> }`.
 * We refuse empty strings defensively (the evaluator treats empty
 * strings as a valid filter value, but they are virtually always a
 * typo on the admin's part — bail early with a clear error).
 */
const filterAllowedValueSchema = z.union([
  z.string().min(1, { message: 'allowed values cannot be empty strings' }),
  z.number(),
]);

/**
 * The `effect` payload of a `setFieldCondition` effect: what to apply to the
 * target field while its predicate holds. All four keys are optional, but at
 * least one must be present — an effect that sets nothing would fold into a
 * condition that can never change anything, which is always an authoring
 * mistake. `checked` is the checkbox-only, edge-triggered value write (tick /
 * untick once when the predicate turns true). `setValue` / `options`
 * (available to a hand-authored `FieldConfig.conditions` leaf) are
 * deliberately NOT on the rule surface.
 */
const ruleFieldConditionEffectSchema = z
  .object({
    visible: z.boolean().optional(),
    disabled: z.boolean().optional(),
    required: z.boolean().optional(),
    checked: z.boolean().optional(),
  })
  .refine(
    (effect) =>
      effect.visible !== undefined ||
      effect.disabled !== undefined ||
      effect.required !== undefined ||
      effect.checked !== undefined,
    {
      message:
        'effect must set at least one of visible, disabled, required, checked',
    },
  );

/**
 * Discriminated union for every supported {@link RuleEffect} type.
 *
 * The `ZodType<RuleEffect>` annotation is there so a mismatch between
 * the TS type and the schema becomes a compile error. Every branch
 * corresponds to exactly one arm of the `RuleEffect` union in
 * `src/components/shared/configurable/types.ts`.
 */
export const ruleEffectSchema: z.ZodType<RuleEffect> = z.discriminatedUnion(
  'type',
  [
    z.object({ type: z.literal('hide') }),
    z.object({ type: z.literal('show') }),
    z.object({ type: z.literal('disable') }),
    z.object({
      type: z.literal('requireProp'),
      prop: z.string().min(1, { message: 'prop is required' }),
    }),
    z.object({
      type: z.literal('setProp'),
      prop: z.string().min(1, { message: 'prop is required' }),
      value: z.unknown(),
    }),
    z.object({
      type: z.literal('filterPropValues'),
      prop: z.string().min(1, { message: 'prop is required' }),
      allowed: z
        .array(filterAllowedValueSchema)
        .min(1, { message: 'allowed must contain at least one value' }),
    }),
    // --- Layout / tooltip effects (accumulated into `_layoutOverrides`) ---
    // Each targets a stable field / column / section `key` (never the label).
    // The shapes here mirror the `RuleEffect` union arm-for-arm and cross to
    // the origami backend whitelist byte-for-byte.
    z.object({
      type: z.literal('renameField'),
      key: z.string().min(1, { message: 'key is required' }),
      label: z.string().min(1, { message: 'label is required' }),
    }),
    z.object({
      type: z.literal('hideField'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    z.object({
      type: z.literal('reorderFields'),
      order: z
        .array(z.string().min(1, { message: 'order keys cannot be empty' }))
        .min(1, { message: 'order must contain at least one key' }),
    }),
    z.object({
      type: z.literal('renameColumn'),
      key: z.string().min(1, { message: 'key is required' }),
      label: z.string().min(1, { message: 'label is required' }),
    }),
    z.object({
      type: z.literal('hideColumn'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    z.object({
      type: z.literal('reorderColumns'),
      order: z
        .array(z.string().min(1, { message: 'order keys cannot be empty' }))
        .min(1, { message: 'order must contain at least one key' }),
    }),
    z.object({
      type: z.literal('reorderSections'),
      order: z
        .array(z.string().min(1, { message: 'order keys cannot be empty' }))
        .min(1, { message: 'order must contain at least one key' }),
    }),
    z.object({
      type: z.literal('setFieldTooltip'),
      key: z.string().min(1, { message: 'key is required' }),
      tooltip: z.string().min(1, { message: 'tooltip is required' }),
    }),
    z.object({
      type: z.literal('setColumnTooltip'),
      key: z.string().min(1, { message: 'key is required' }),
      tooltip: z.string().min(1, { message: 'tooltip is required' }),
      target: z.enum(['header', 'cell']).optional(),
    }),
    // Per-tenant DataList default sort (Tier 1 data-row ordering). `column`
    // is a stable column key; `direction` is asc/desc. Mirrors the origami
    // backend whitelist (`validateSetDefaultSort`).
    z.object({
      type: z.literal('setDefaultSort'),
      column: z.string().min(1, { message: 'column is required' }),
      direction: z.enum(['asc', 'desc']),
    }),
    // Per-tenant DataList manual row order (Tier 2 data-row ordering). `order`
    // lists stable row ids in desired sequence. Mirrors the origami backend
    // whitelist (`validateReorderRows`).
    z.object({
      type: z.literal('reorderRows'),
      order: z
        .array(z.string().min(1, { message: 'order keys cannot be empty' }))
        .min(1, { message: 'order must contain at least one row id' }),
    }),
    // --- Free-text editing effects (layout-editor free-text plan, P1) ---
    // `placeholder` / `helperText` accept the empty string so a baseline
    // placeholder / helper text can be forced off; tooltips keep dedicated
    // `clear*Tooltip` arms instead of an empty setter.
    z.object({
      type: z.literal('setFieldPlaceholder'),
      key: z.string().min(1, { message: 'key is required' }),
      placeholder: z.string(),
    }),
    z.object({
      type: z.literal('setFieldHelperText'),
      key: z.string().min(1, { message: 'key is required' }),
      helperText: z.string(),
    }),
    // Add-only client-side "required" effect. `message` is optional — the
    // evaluator falls back to `DEFAULT_REQUIRED_MESSAGE` when omitted/blank.
    z.object({
      type: z.literal('requireField'),
      key: z.string().min(1, { message: 'key is required' }),
      message: z.string().optional(),
    }),
    z.object({
      type: z.literal('setTitle'),
      title: z.string().min(1, { message: 'title is required' }),
    }),
    z.object({
      type: z.literal('setActionLabel'),
      action: z.string().min(1, { message: 'action is required' }),
      label: z.string().min(1, { message: 'label is required' }),
    }),
    z.object({
      type: z.literal('hideAction'),
      action: z.string().min(1, { message: 'action is required' }),
    }),
    z.object({
      type: z.literal('clearFieldTooltip'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    z.object({
      type: z.literal('clearColumnTooltip'),
      key: z.string().min(1, { message: 'key is required' }),
      target: z.enum(['header', 'cell']).optional(),
    }),
    z.object({
      type: z.literal('showField'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    z.object({
      type: z.literal('showColumn'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    // --- DataList toolbar-filter / search effects ---------------------------
    // Target the stable toolbar filter `id` (never the label). Fold into
    // `_layoutOverrides.filters` / `.search`. Mirror the column/search arms and
    // cross to the origami backend whitelist byte-for-byte.
    z.object({
      type: z.literal('renameFilter'),
      key: z.string().min(1, { message: 'key is required' }),
      label: z.string().min(1, { message: 'label is required' }),
    }),
    z.object({
      type: z.literal('hideFilter'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    z.object({
      type: z.literal('showFilter'),
      key: z.string().min(1, { message: 'key is required' }),
    }),
    z.object({
      type: z.literal('reorderFilters'),
      order: z
        .array(z.string().min(1, { message: 'order keys cannot be empty' }))
        .min(1, { message: 'order must contain at least one key' }),
    }),
    // `placeholder` accepts the empty string so a baseline placeholder can be
    // forced off (mirrors `setFieldPlaceholder`).
    z.object({
      type: z.literal('setFilterPlaceholder'),
      key: z.string().min(1, { message: 'key is required' }),
      placeholder: z.string(),
    }),
    z.object({
      type: z.literal('setSearchPlaceholder'),
      placeholder: z.string(),
    }),
    z.object({ type: z.literal('hideSearch') }),
    z.object({ type: z.literal('showSearch') }),
    // --- Runtime form-value condition ---------------------------------------
    // `key` is the TARGET field; `when` is the stable key of a SOURCE **form
    // field of the same component** (never a rule-context variable). `value` is
    // optional (omitted for isEmpty / isNotEmpty). `effect` must carry at least
    // one of visible / disabled / required / checked — an empty effect would
    // fold into a condition that can never change anything, so it is rejected
    // at authoring time. Mirrors the origami backend whitelist byte-for-byte.
    z.object({
      type: z.literal('setFieldCondition'),
      key: z.string().min(1, { message: 'key is required' }),
      when: z.string().min(1, { message: 'when is required' }),
      operator: conditionOperatorSchema,
      value: z.unknown().optional(),
      effect: ruleFieldConditionEffectSchema,
    }),
  ],
);

// ---------------------------------------------------------------------------
// Condition tree schemas (component-rules variant)
// ---------------------------------------------------------------------------

/**
 * Zod schema for a {@link SimpleCondition} leaf. Component-rule leaves
 * use `field` (not form-rules' `when`) and have no per-leaf `effect` —
 * the outer {@link RuleEffect} array carries the actions instead.
 */
export const componentSimpleConditionSchema: z.ZodType<SimpleCondition> = z.object({
  field: z.string().min(1, { message: 'field is required' }),
  operator: conditionOperatorSchema,
  value: z.unknown(),
});

/**
 * Recursive Zod schema for a {@link ConditionGroup}. The `conditions`
 * array contains a mix of leaves and nested groups, both validated by
 * {@link componentConditionNodeSchema} (declared below — referenced via
 * {@link z.lazy} so the mutual recursion resolves at parse time).
 */
export const componentConditionGroupSchema: z.ZodType<ConditionGroup> = z.lazy(
  () =>
    z.object({
      operator: z.enum(['AND', 'OR']),
      conditions: z.array(componentConditionNodeSchema),
    }),
);

/**
 * Zod schema for any node in the component-rule condition tree — leaf or
 * nested group. Used inside {@link componentConditionGroupSchema} via
 * {@link z.lazy} to allow arbitrarily nested AND/OR boolean trees.
 */
export const componentConditionNodeSchema: z.ZodType<FieldCondition> = z.lazy(
  () =>
    z.union([componentSimpleConditionSchema, componentConditionGroupSchema]),
);

// ---------------------------------------------------------------------------
// Rule JSON schema
// ---------------------------------------------------------------------------

/**
 * Zod schema for the complete rule JSON body: a condition tree plus an
 * ordered list of effects.
 *
 * Matches the {@link ComponentRuleJsonDTO} TypeScript interface and the
 * {@link ComponentRule} interface's `conditions` + `effects` fields.
 */
export const ruleJsonSchema = z.object({
  conditions: componentConditionGroupSchema,
  effects: z.array(ruleEffectSchema),
});

/**
 * Convenience alias mirroring the `z.infer` output. Kept exported so
 * callers do not need to repeat the `z.infer<typeof ruleJsonSchema>`
 * boilerplate. Equivalent to `ComponentRuleJsonDTO` from the service
 * layer, but typed locally to the configurable layer.
 */
export type RuleJson = z.infer<typeof ruleJsonSchema>;
